/**
 * Central source of truth for all user roles in the application
 * Used by AddNewUserModal and other user management components
 */

import { Edit } from 'react-feather';
import { splitCapitalizedString } from './common.util';

// Main role categories
export const MAIN_ROLES = {
  ACADEMIC_YEAR: 'AcademicYearAccess',
  ACADEMIC_YEAR_READ_ONLY: 'AcademicYearAccessReadOnly',
  BUDGET: 'BudgetAccess',
  BUDGET_READ_ONLY: 'BudgetAccessReadOnly',
  REVENUE: 'RevenueAccess',
  REVENUE_READ_ONLY: 'RevenueAccessReadOnly',
  EXPENSES: 'ExpensesAccess',
  EXPENSES_READ_ONLY: 'ExpensesAccessReadOnly',
  STUDENTS: 'StudentsAccess',
  STUDENTS_READ_ONLY: 'StudentsAccessReadOnly',
  DAILY_BILLING_FEES: 'DailyBillingFeesAccess',
  DAILY_BILLING_FEES_READ_ONLY: 'DailyBillingFeesAccessReadOnly',
  CLASS: 'ClassAccess',
  CLASS_READ_ONLY: 'ClassAccessReadOnly',
  BILLING: 'BillingAccess',
  BILLING_READ_ONLY: 'BillingAccessReadOnly',
  EMPLOYEE: 'EmployeeAccess',
  EMPLOYEE_READ_ONLY: 'EmployeeAccessReadOnly',
  PAYROLL: 'PayrollAccess',
  PAYROLL_READ_ONLY: 'PayrollAccessReadOnly',
  SUPPLIERS: 'SuppliersAccess',
  SUPPLIERS_READ_ONLY: 'SuppliersAccessReadOnly',
  SUBSCRIPTION: 'SubscriptionAccess',
  SUBSCRIPTION_READ_ONLY: 'SubscriptionAccessReadOnly',
  FIXED_ASSET: 'FixedAssetAccess',
  FIXED_ASSET_READ_ONLY: 'FixedAssetAccessReadOnly',
  LEDGERS: 'LedgersAccess',
  LEDGERS_READ_ONLY: 'LedgersAccessReadOnly',
  SETTINGS: 'SettingsAccess',
  SETTINGS_READ_ONLY: 'SettingsAccessReadOnly',
  JOURNAL_VOUCHER: 'JournalVoucherAccess',
  JOURNAL_VOUCHER_READ_ONLY: 'JournalVoucherAccessReadOnly',
  MESSAGING: 'MessagingAccess',
  MESSAGING_READ_ONLY: 'MessagingAccessReadOnly',
  ANALYTICS: 'AnalyticsAccess',
  ACADEMIC: 'AcademicAccess',
  ACADEMIC_READ_ONLY: 'AcademicAccessReadOnly',
};

// Revenue sub-roles (shown conditionally when RevenueAccess is selected)
export const REVENUE_SUB_ROLES = {
  REVENUE_BACK_DATING: 'RevenueBackDatingAccess',
  REVENUE_REVERSAL: 'RevenueReversalAccess',
  ONLINE_REVENUE_APPLICATION: 'OnlineRevenueApplicationAccess',
};

// Daily Billing Fees sub-roles (shown conditionally when DailyBillingFeesAccess or DailyBillingFeesAccessReadOnly is selected)
// export const DAILY_FEES_SUB_ROLES = [
//   {
//     id: 'DailyBillingFeesAdvancePaymentAccess',
//     name: 'Advance Payment Access',
//   },
//   {
//     id: 'DailyBillingFeesAdvancePaymentAccessReadOnly',
//     name: 'Advance Payment Access Read Only',
//   },
//   {
//     id: 'DailyBillingFeesTypeCreationAccess',
//     name: 'Fee Type Creation Access',
//   },
//   {
//     id: 'DailyBillingFeesTypeCreationAccessReadOnly',
//     name: 'Fee Type Creation Access Read Only',
//   },
//   {
//     id: 'DailyBillingFeesReportAccess',
//     name: 'Report Access',
//   },
//   {
//     id: 'DailyBillingFeesReportAccessReadOnly',
//     name: 'Report Access Read Only',
//   },
//   {
//     id: 'DailyBillingFeesSettingsAccess',
//     name: 'Settings Access',
//   },
//   {
//     id: 'DailyBillingFeesSettingsAccessReadOnly',
//     name: 'Settings Access Read Only',
//   },
//   {
//     id: 'DailyBillingFeesStudentBillAccess',
//     name: 'Student Bill Access',
//   },
//   {
//     id: 'DailyBillingFeesStudentBillAccessReadOnly',
//     name: 'Student Bill Access Read Only',
//   },
// ];
// rewrite academic subroles to the format of that of the billing sub roles
export const DAILY_FEES_SUB_ROLES = {
  ADVANCE_PAYMENT_ACCESS: 'DailyBillingFeesAdvancePaymentAccess',
  ADVANCE_PAYMENT_ACCESS_READ_ONLY:
    'DailyBillingFeesAdvancePaymentAccessReadOnly',
  FEE_TYPE_CREATION_ACCESS: 'DailyBillingFeesTypeCreationAccess',
  FEE_TYPE_CREATION_ACCESS_READ_ONLY:
    'DailyBillingFeesTypeCreationAccessReadOnly',
  REPORT_ACCESS: 'DailyBillingFeesReportAccess',
  REPORT_ACCESS_READ_ONLY: 'DailyBillingFeesReportAccessReadOnly',
  SETTINGS_ACCESS: 'DailyBillingFeesSettingsAccess',
  SETTINGS_ACCESS_READ_ONLY: 'DailyBillingFeesSettingsAccessReadOnly',
  STUDENT_BILL_ACCESS: 'DailyBillingFeesStudentBillAccess',
  STUDENT_BILL_ACCESS_READ_ONLY: 'DailyBillingFeesStudentBillAccessReadOnly',
};

// Academic sub-roles (shown conditionally when AcademicAccess is selected)
// export const ACADEMIC_SUB_ROLES = [
//   {
//     id: 'AcademicReportPercentagesAccess',
//     name: 'Academic Report Percentages Access',
//   },
//   {
//     id: 'AcademicReportPercentagesAccessReadOnly',
//     name: 'Academic Report Percentages Access Read Only',
//   },
//   {
//     id: 'AcademicReportRemarksAccess',
//     name: 'Academic Report Remarks Access',
//   },
//   {
//     id: 'AcademicReportRemarksAccessReadOnly',
//     name: 'Academic Report Remarks Access Read Only',
//   },
//   {
//     id: 'AcademicReportGenerationAccess',
//     name: 'Academic Report Generation Access',
//   },
//   {
//     id: 'AcademicReportSBAConfigurationAccess',
//     name: 'Academic Report SBA Configuration Access',
//   },
//   {
//     id: 'AttendanceBackdatingAccess',
//     name: 'Attendance Backdating Access',
//   },
// ];
// rewrite academic subroles to the format of that of the billing sub roles
export const ACADEMIC_SUB_ROLES = {
  ACADEMIC_REPORT_PERCENTAGES_ACCESS: 'AcademicReportPercentagesAccess',
  ACADEMIC_REPORT_PERCENTAGES_ACCESS_READ_ONLY:
    'AcademicReportPercentagesAccessReadOnly',
  ACADEMIC_REPORT_REMARKS_ACCESS: 'AcademicReportRemarksAccess',
  ACADEMIC_REPORT_REMARKS_ACCESS_READ_ONLY:
    'AcademicReportRemarksAccessReadOnly',
  ACADEMIC_REPORT_GENERATION_ACCESS: 'AcademicReportGenerationAccess',
  ACADEMIC_REPORT_SBA_CONFIGURATION_ACCESS:
    'AcademicReportSBAConfigurationAccess',
  ATTENDANCE_BACKDATING_ACCESS: 'AttendanceBackdatingAccess',
  ATTENDANCE_TOTAL_DAYS_ACCESS: 'AttendanceTotalDaysAccess',
  EDIT_PREVIOUS_ATTENDANCE_ACCESS: 'AttendanceEditAccess',
};

// billing sub roles
export const BILLING_SUB_ROLES = {
  BILLING_REPORT_ACCESS: 'BillingReportAccess',
  CREATE_BILLING_FEE_ACCESS: 'CreateBillingFeeAccess',
  VOID_STUDENT_BILL_ACCESS: 'VoidStudentBillAccess',
  BILL_STUDENT_ACCESS: 'BillStudentAccess',
  FINANCIAL_AID_ACCESS: 'FinancialAidAccess',
  FINANCIAL_AID_ACCESS_READ_ONLY: 'FinancialAidAccessReadOnly',
  FINANCIAL_AID_APPROVAL_ACCESS: 'FinancialAidApprovalAccess',
};

// Class Access sub roles
export const CLASS_SUB_ROLES = {
  CLASS_MANAGEMENT_ACCESS: 'ClassManagementAccess',
};

// Helper function to get all roles (flattened for easier checking)
export const getAllRoles = () => {
  const allRoles = [
    ...Object.values(MAIN_ROLES),
    ...Object.values(REVENUE_SUB_ROLES),
    ...convertUnderscoredObjectToArray(DAILY_FEES_SUB_ROLES).map((r) => r.id),
    ...ACADEMIC_SUB_ROLES.map((r) => r.id),
  ];
  return allRoles;
};

// Helper function to get display name for a role
export const getRoleDisplayName = (roleId) => {
  // Check in sub-roles arrays
  const dailyFeeRole = convertUnderscoredObjectToArray(
    DAILY_FEES_SUB_ROLES,
  ).find((r) => r.id === roleId);
  if (dailyFeeRole) return dailyFeeRole.name;

  const academicRole = ACADEMIC_SUB_ROLES.find((r) => r.id === roleId);
  if (academicRole) return academicRole.name;

  // Default: try to split camelCase and add "Access"
  return roleId.replace(/([A-Z])/g, ' $1').trim();
};

// convert an underscored object roles(e.g BILLING_SUB_ROLES) to array of objects{id, name}(e.g ACADEMIC_SUB_ROLES)
export const convertUnderscoredObjectToArray = (roles) => {
  return Object.entries(roles).map(([key, value]) => ({
    id: value,
    name: splitCapitalizedString(value),
  }));
};
